import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useI18n, useLogger } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';

const AppSettings: FC = () => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (App Settings)', { fields: data });

    // If auto accept is enabled or hide incoming app orders, we need to disable the auto show incoming app popup since it does not make sense together.
    if (data.autoAcceptAppOrders || data.showIncomingAppOrders === false) {
      data.autoShowIncomingAppPopup = false;
    }

    commitToStorage(data);
  }

  const togglesConfig: Array<{
    label: string;
    subLabel?: string;
    key: string;
    storage: any;
    inverted?: boolean;
    disabled?: boolean;
  }> = [];

  togglesConfig.push(
    {
      label: i18n('Settings.APP.AutoAccept'),
      subLabel: i18n('Settings.APP.AutoAccept.Text'),
      key: 'autoAcceptAppOrders',
      storage: storage.autoAcceptAppOrders,
    },
    {
      label: i18n('Settings.APP.ShowIncomingAppOrders'),
      subLabel: i18n('Settings.APP.ShowIncomingAppOrders.Text'),
      key: 'showIncomingAppOrders',
      storage: storage.showIncomingAppOrders,
    },
  );

  if (!storage.autoAcceptAppOrders && storage.showIncomingAppOrders) {
    togglesConfig.push({
      label: i18n('Settings.APP.AutoShowOnlinePopup'),
      subLabel: i18n('Settings.APP.AutoShowOnlinePopup.Text'),
      key: 'autoShowIncomingAppPopup',
      storage: storage.autoShowIncomingAppPopup,
    });
  }

  togglesConfig.push(
    {
      label: i18n('Settings.NewAppOrderingKDS'),
      subLabel: i18n('Settings.NewAppOrderingKDS.Text'),
      key: 'enableNewKDSAppOrders',
      storage: storage.enableNewKDSAppOrders,
    },
    {
      label: i18n('Settings.CompleteAppOrdersInstantly'),
      key: 'completeAllAppOrdersInstantly',
      storage: storage.completeAllAppOrdersInstantly,
    },
    {
      label: i18n('Settings.OnlinePlayNewOrderSound'),
      key: 'onlinePlayNewOrderSound',
      storage: storage.onlinePlayNewOrderSound,
    },
  );

  if (storageLoading) {
    return null;
  }

  return (
    <Box padding="large">
      <SettingsBlock>
        {togglesConfig.map((toggle) => (
          <Toggle
            key={toggle.key}
            settingKey={toggle.key}
            label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
            description={toggle.subLabel}
            initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
            onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
            fullWidth
            darkBackground
            lightText
          />
        ))}
      </SettingsBlock>
    </Box>
  );
};

export default AppSettings;
